import { getDocumentUnitName } from "./unitNames";

const UNIT_LEVELS = ["unit", "subunit", "subunit2"];

function localizedNameForId(source, id, unitMap, languageId) {
  if (languageId === undefined || id == null) return "";
  const candidates = [source.unit, source.subunit, source.subunit2,
    { id: source.unit_id, name: source.selected_unit_name, name_translations: source.selected_unit_name_translations },
    { id: source.inventory_unit_id, name: source.inventory_unit_name, name_translations: source.inventory_unit_name_translations },
    ...UNIT_LEVELS.map((level) => ({ id: source[`item_${level}_id`], name: source[`${level}_name`], name_translations: source[`${level}_name_translations`] }))];
  const matching = candidates.filter((unit) => unit && String(unit.id) === String(id));
  const unit = matching.find((entry) => entry.name_translations && Object.keys(entry.name_translations).length) || matching.find((entry) => entry.name);
  return getDocumentUnitName(unit, id, unitMap, languageId);
}

const firstPresent = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const positiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const numberOrNull = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const valueFromUnitMap = (value, unitMap) => {
  if (!value) return "";
  if (typeof value === "object") return value.name || "";
  if (unitMap instanceof Map) {
    const unit = unitMap.get(String(value));
    return typeof unit === "object" ? unit?.name || "" : unit || "";
  }
  if (Array.isArray(unitMap)) {
    return unitMap.find((unit) => String(unit?.id) === String(value))?.name || "";
  }
  return "";
};

const unitIdFor = (source = {}, level) => {
  if (level === "unit") {
    return firstPresent(
      source.inventory_unit_id,
      source.item_unit_id,
      source.unit?.id,
      source.unit_id
    );
  }
  if (level === "subunit") {
    return firstPresent(source.item_subunit_id, source.subunit?.id, source.subunit_id);
  }
  return firstPresent(source.item_subunit2_id, source.subunit2?.id, source.subunit2_id);
};

const unitNameFor = (source = {}, level, unitMap, languageId) => {
  const localized = localizedNameForId(source, unitIdFor(source, level), unitMap, languageId);
  if (localized) return localized;
  if (level === "unit") {
    return firstPresent(
      source.inventory_unit_name,
      source.item_unit_name,
      source.unit?.name,
      source.unit_name,
      valueFromUnitMap(unitIdFor(source, level), unitMap)
    ) || "";
  }
  if (level === "subunit") {
    return firstPresent(
      source.item_subunit_name,
      source.subunit?.name,
      source.subunit_name,
      valueFromUnitMap(unitIdFor(source, level), unitMap)
    ) || "";
  }
  return firstPresent(
    source.item_subunit2_name,
    source.subunit2?.name,
    source.subunit2_name,
    valueFromUnitMap(unitIdFor(source, level), unitMap)
  ) || "";
};

export const formatUnitNumber = (value, maximumFractionDigits = 8) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return new Intl.NumberFormat("en-GB", {
    maximumFractionDigits,
    useGrouping: true,
  }).format(number);
};

export function buildDeliveryNoteUnitOptions(source = {}, unitMap, languageId) {
  const mainId = unitIdFor(source, "unit");
  const mainLabel = unitNameFor(source, "unit", unitMap, languageId);
  const subunitId = unitIdFor(source, "subunit");
  const subunitLabel = unitNameFor(source, "subunit", unitMap, languageId);
  const subunit2Id = unitIdFor(source, "subunit2");
  const subunit2Label = unitNameFor(source, "subunit2", unitMap, languageId);
  const packSize = positiveNumber(source.pack_size);
  const subunit2Size = positiveNumber(source.subunit2_size ?? source.pack_size_2 ?? source.pack_size2);

  const options = [];
  if (mainId || mainLabel) {
    options.push({
      level: "unit",
      unitId: mainId || null,
      label: mainLabel || "Main unit",
      factor: 1,
      inventoryUnitId: mainId || null,
      inventoryLabel: mainLabel || "Main unit",
    });
  }
  if ((subunitId || subunitLabel) && packSize) {
    options.push({
      level: "subunit",
      unitId: subunitId || null,
      label: subunitLabel || "Subunit",
      factor: packSize,
      inventoryUnitId: mainId || null,
      inventoryLabel: mainLabel || "Main unit",
    });
  }
  if ((subunit2Id || subunit2Label) && packSize && subunit2Size && options.some((option) => option.level === "subunit")) {
    options.push({
      level: "subunit2",
      unitId: subunit2Id || null,
      label: subunit2Label || "Second subunit",
      factor: packSize * subunit2Size,
      inventoryUnitId: mainId || null,
      inventoryLabel: mainLabel || "Main unit",
    });
  }

  const persistedLevel = source.__persisted_unit_level;
  const persistedFactor = positiveNumber(source.__persisted_unit_factor);
  if (UNIT_LEVELS.includes(persistedLevel) && persistedFactor) {
    const persistedOption = {
      level: persistedLevel,
      unitId: firstPresent(source.__persisted_unit_id, null),
      label: firstPresent(localizedNameForId(source, source.__persisted_unit_id, unitMap, languageId), source.__persisted_selected_unit_name, "Selected unit"),
      factor: persistedFactor,
      inventoryUnitId: firstPresent(source.__persisted_inventory_unit_id, mainId, null),
      inventoryLabel: firstPresent(localizedNameForId(source, source.__persisted_inventory_unit_id, unitMap, languageId), source.__persisted_inventory_unit_name, mainLabel, "Main unit"),
    };
    const existingIndex = options.findIndex((option) => option.level === persistedLevel);
    if (existingIndex >= 0) options[existingIndex] = { ...options[existingIndex], ...persistedOption };
    else options.push(persistedOption);
    options.sort((left, right) => UNIT_LEVELS.indexOf(left.level) - UNIT_LEVELS.indexOf(right.level));
  }

  return options;
}

export function unitHierarchyLabel(source = {}, unitMap, languageId) {
  const options = buildDeliveryNoteUnitOptions(source, unitMap, languageId);
  const main = options.find((option) => option.level === "unit");
  const subunit = options.find((option) => option.level === "subunit");
  const subunit2 = options.find((option) => option.level === "subunit2");
  if (!main) return "Unit not configured";

  const relationships = [];
  if (subunit) {
    relationships.push(`1 ${main.label} = ${formatUnitNumber(subunit.factor)} ${subunit.label}`);
  }
  if (subunit && subunit2) {
    const perSubunit = subunit2.factor / subunit.factor;
    relationships.push(`1 ${subunit.label} = ${formatUnitNumber(perSubunit)} ${subunit2.label}`);
  } else if (subunit2) {
    relationships.push(`1 ${main.label} = ${formatUnitNumber(subunit2.factor)} ${subunit2.label}`);
  }
  return relationships.length ? relationships.join(" · ") : `Inventory unit: ${main.label}`;
}

export function resolveDeliveryNoteUnit(source = {}, requestedLevel, unitMap, languageId) {
  const options = buildDeliveryNoteUnitOptions(source, unitMap, languageId);
  const level = UNIT_LEVELS.includes(requestedLevel) ? requestedLevel : "unit";
  return options.find((option) => option.level === level) || options[0] || null;
}

export function isDeliveryNoteUnitLevelValid(source = {}, requestedLevel, unitMap) {
  if (!UNIT_LEVELS.includes(requestedLevel)) return false;
  return buildDeliveryNoteUnitOptions(source, unitMap).some((option) => option.level === requestedLevel);
}

export function defaultDeliveryNoteUnitLevel(source = {}, { requireExplicitWhenConvertible = false, unitMap } = {}) {
  const options = buildDeliveryNoteUnitOptions(source, unitMap);
  if (!options.length) return "";
  if (requireExplicitWhenConvertible && options.length > 1) return "";
  return options[0].level;
}

export function deliveryNoteInventoryValues(line = {}, source = line, unitMap, languageId) {
  const option = resolveDeliveryNoteUnit(source, line?.unit_level, unitMap, languageId);
  const rawQuantity = numberOrNull(line?.quantity_in_dn ?? line?.quantity);
  const rawUnitPrice = numberOrNull(line?.unit_price_in_dn ?? line?.unit_price);
  const hasQuantity = rawQuantity !== null;
  const hasPrice = rawUnitPrice !== null;
  const factor = option?.factor || 1;

  return {
    option,
    factor,
    quantity: hasQuantity ? rawQuantity / factor : null,
    unitPrice: hasPrice ? rawUnitPrice * factor : null,
    total: hasQuantity && hasPrice ? rawQuantity * rawUnitPrice : null,
  };
}

export function deliveryNoteInventoryPreview(line = {}, source = line, unitMap, languageId) {
  const values = deliveryNoteInventoryValues(line, source, unitMap, languageId);
  if (!values.option) return "Select a valid unit";
  if (values.quantity === null) return `Inventory unit: ${values.option.inventoryLabel}`;

  const quantityText = `${formatUnitNumber(values.quantity)} ${values.option.inventoryLabel}`;
  if (values.unitPrice === null) return `Inventory: ${quantityText}`;
  return `Inventory: ${quantityText} @ ${formatUnitNumber(values.unitPrice, 8)} EUR/${values.option.inventoryLabel}`;
}

export function mergeDeliveryNoteUnitSource(item, line) {
  if (!item) return line || {};
  if (!line) return item;
  return {
    ...item,
    ...line,
    unit: item.unit || line.unit,
    subunit: item.subunit || line.subunit,
    subunit2: item.subunit2 || line.subunit2,
    item_unit_id: firstPresent(line.item_unit_id, item.item_unit_id, item.unit_id, item.unit?.id),
    item_subunit_id: firstPresent(line.item_subunit_id, item.item_subunit_id, item.subunit_id, item.subunit?.id),
    item_subunit2_id: firstPresent(line.item_subunit2_id, item.item_subunit2_id, item.subunit2_id, item.subunit2?.id),
  };
}

// Explicit editing refresh: document quantities/prices stay unchanged; historical
// callers continue to use their saved conversion until editing is requested.
export function refreshDeliveryNoteLineUnits(line, item, { preserveSnapshot = false } = {}) {
  if (!item) throw new Error("Current item units could not be loaded. Reload the delivery note before editing.");
  const options = buildDeliveryNoteUnitOptions(item);
  const level = line.unit_level || "unit";
  const option = (preserveSnapshot ? buildDeliveryNoteUnitOptions(line) : options).find((entry) => entry.level === level);
  if (!option) throw new Error("The item's units have changed. Review its unit configuration before editing this delivery note.");
  const previousInventoryId = firstPresent(line.__persisted_inventory_unit_id, line.inventory_unit_id, line.item_unit_id);
  const previousSelectedId = firstPresent(line.__persisted_unit_id, line.unit_id);
  if ((previousInventoryId != null && String(previousInventoryId) !== String(options[0]?.inventoryUnitId)) ||
      (!preserveSnapshot && previousSelectedId != null && String(previousSelectedId) !== String(option.unitId))) {
    throw new Error("The item's units have changed. Review its unit configuration before editing this delivery note.");
  }
  const main = options.find((entry) => entry.level === "unit");
  const subunit = options.find((entry) => entry.level === "subunit");
  const subunit2 = options.find((entry) => entry.level === "subunit2");
  return {
    ...line,
    unit: item.unit, subunit: item.subunit, subunit2: item.subunit2,
    item_unit_id: main?.unitId, item_unit_name: main?.label,
    item_subunit_id: subunit?.unitId ?? null, item_subunit_name: subunit?.label || "",
    subunit_id: subunit?.unitId ?? null, subunit_name: subunit?.label || "",
    item_subunit2_id: subunit2?.unitId ?? null, item_subunit2_name: subunit2?.label || "",
    subunit2_id: subunit2?.unitId ?? null, subunit2_name: subunit2?.label || "",
    pack_size: subunit?.factor ?? null, subunit2_size: subunit2 ? subunit2.factor / subunit.factor : null,
    pack_size_2: null, pack_size2: null,
    unit_level: option.level, unit_factor: option.factor, unit_id: option.unitId,
    selected_unit_name: option.label, inventory_unit_id: option.inventoryUnitId, inventory_unit_name: option.inventoryLabel,
    __persisted_unit_level: option.level, __persisted_unit_factor: option.factor, __persisted_unit_id: option.unitId,
    __persisted_selected_unit_name: option.label, __persisted_inventory_unit_id: option.inventoryUnitId,
    __persisted_inventory_unit_name: option.inventoryLabel,
  };
}
